import React, { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { TrendingUp, TrendingDown, Wallet, AlertTriangle, Building2, X, Users, Package, HardHat, Loader2, UserRound } from "lucide-react";

const Kpi = ({ label, value, cls = "text-slate-900", sub }) => (
  <div className="px-4 py-3">
    <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`mt-0.5 text-lg font-bold tabular-nums ${cls}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
  </div>
);

function JobDetailModal({ ticketId, onClose }) {
  const { t } = useLang();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/financials/job/${ticketId}`).then(setD).catch(() => onClose());
  }, [ticketId]); // eslint-disable-line

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="job-detail-modal" className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {!d ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{d.ticket.title}</h2>
                <div className="text-xs text-slate-400">
                  {d.ticket.number} · {d.ticket.unit_name}{d.ticket.client_name ? ` · ${d.ticket.client_name}` : ""}
                </div>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="border border-slate-200 rounded-xl grid grid-cols-1 sm:grid-cols-3 divide-x divide-slate-200">
                <Kpi label={t("budget")} value={money(d.summary.budget_total)}
                  sub={`${t("labor")} ${money(d.summary.budget_labor)} · ${t("material")} ${money(d.summary.budget_material)}`} />
                <Kpi label={t("real")} value={money(d.summary.actual_total)}
                  sub={`${t("labor")} ${money(d.summary.actual_labor)} · ${t("material")} ${money(d.summary.actual_material)}`} />
                <Kpi label={t("variance")} value={money(d.summary.variance_total)}
                  cls={d.summary.variance_total < 0 ? "text-red-600" : "text-emerald-600"}
                  sub={`${t("labor")} ${money(d.summary.variance_labor)} · ${t("material")} ${money(d.summary.variance_material)}`} />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-slate-500" />
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("whoWorked")}</span>
                </div>
                {d.workers.length === 0 && !d.summary.is_subcontractor && <p className="text-xs text-slate-400">{t("noLabor")}</p>}
                {d.workers.length > 0 && (
                  <table className="w-full text-sm tabular-nums">
                    <thead>
                      <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-200">
                        <th className="text-left py-1.5">{t("name")}</th>
                        <th className="text-right py-1.5">{t("hours")}</th>
                        <th className="text-right py-1.5">{t("laborCost")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.workers.map((w) => (
                        <tr key={w.tech_id} data-testid={`job-worker-${w.tech_id}`} className="border-b border-slate-100">
                          <td className="py-2 font-medium text-slate-800 flex items-center gap-1.5"><UserRound className="w-3.5 h-3.5 text-slate-400" />{w.tech_name}</td>
                          <td className="py-2 text-right text-slate-600">{w.hours}h</td>
                          <td className="py-2 text-right font-semibold text-slate-900">{money(w.cost)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td className="py-2 text-xs text-slate-400">{t("total")}</td>
                        <td className="py-2 text-right text-slate-600 font-medium">{d.summary.tech_hours}h</td>
                        <td className="py-2 text-right font-bold text-slate-900">{money(d.summary.tech_labor)}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
                {d.summary.is_subcontractor && (
                  <div className="mt-2 bg-fuchsia-50 border border-fuchsia-200 rounded-lg px-4 py-3 text-sm flex flex-wrap items-center gap-x-5 gap-y-1">
                    <span className="font-semibold text-fuchsia-800 flex items-center gap-1.5"><HardHat className="w-4 h-4" />{d.summary.subcontractor_name || t("subcontractor")}</span>
                    <span className="text-slate-600">{t("subcontractor")}: <b>{money(d.summary.subcontract_value)}</b></span>
                    <span className="text-slate-600">{Math.round((d.summary.progress || 0) * 100)}% · <b>{money(d.summary.sub_earned)}</b></span>
                    <span className="text-slate-600">{t("paid")}: <b>{money(d.summary.sub_paid)}</b></span>
                    <span className="text-fuchsia-700 font-bold">{t("due")}: {money(d.summary.sub_due)}</span>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-4 h-4 text-slate-500" />
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("materials")}</span>
                </div>
                {d.materials.length === 0 ? <p className="text-xs text-slate-400">{t("noMaterials")}</p> : (
                  <table className="w-full text-sm tabular-nums">
                    <thead>
                      <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-200">
                        <th className="text-left py-1.5">Item</th>
                        <th className="text-right py-1.5">Qty</th>
                        <th className="text-left py-1.5 pl-4">{t("supplier")}</th>
                        <th className="text-left py-1.5">PO</th>
                        <th className="text-right py-1.5">{t("total")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.materials.map((m, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-2 text-slate-800">{m.description}</td>
                          <td className="py-2 text-right text-slate-500">{m.quantity}</td>
                          <td className="py-2 pl-4 text-slate-600">{m.supplier || "—"}</td>
                          <td className="py-2 text-xs text-violet-600 font-medium">{m.source}</td>
                          <td className="py-2 text-right font-semibold text-slate-900">{money(m.total)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={4} className="py-2 text-xs text-slate-400">{t("total")}</td>
                        <td className="py-2 text-right font-bold text-slate-900">{money(d.summary.actual_material)}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Financials() {
  const { t } = useLang();
  const [tab, setTab] = useState("overall");
  const [data, setData] = useState(null);
  const [props, setProps] = useState(null);
  const [clients, setClients] = useState(null);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    api.get("/financials").then(setData).catch(() => {});
    api.get("/financials/by-property").then((r) => setProps(r.properties || [])).catch(() => {});
    api.get("/financials/by-client").then((r) => setClients(r.clients || [])).catch(() => {});
  }, []);
  if (!data) return <div className="text-slate-400 text-sm">Loading…</div>;

  const { rows, totals } = data;
  const variancePos = totals.variance_total >= 0;
  const overList = (props || []).filter((p) => p.over_budget);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("financials")}</h1>
        <p className="text-slate-500 text-sm mt-1">{t("costSummary")}</p>
      </div>

      {overList.length > 0 && (
        <div data-testid="over-budget-alert" className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">
            <b>{overList.length}</b> · {t("overBudget")}: {overList.map((p) => p.property_name).join(", ")}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-200">
        <div className="px-5 py-4"><div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{t("budget")}</div><div className="mt-1 text-2xl font-bold text-slate-900">{money(totals.budget_total)}</div></div>
        <div className="px-5 py-4"><div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{t("real")}</div><div className="mt-1 text-2xl font-bold text-slate-900">{money(totals.actual_total)}</div></div>
        <div className="px-5 py-4"><div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{t("variance")}</div><div className={`mt-1 text-2xl font-bold flex items-center gap-1 ${variancePos ? "text-emerald-600" : "text-red-600"}`}>{variancePos ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}{money(totals.variance_total)}</div></div>
        <div className="px-5 py-4"><div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{t("subcontractor")} {t("due")}</div><div className="mt-1 text-2xl font-bold text-fuchsia-600 flex items-center gap-1"><Wallet className="w-5 h-5" />{money(totals.sub_due)}</div></div>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        <button data-testid="fin-tab-overall" onClick={() => setTab("overall")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "overall" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{t("overall")}</button>
        <button data-testid="fin-tab-property" onClick={() => setTab("property")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "property" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{t("byProperty")}</button>
        <button data-testid="fin-tab-client" onClick={() => setTab("client")} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "client" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{t("byClient")}</button>
      </div>

      {tab === "overall" && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="text-left px-4 py-3">Job</th>
                  <th className="text-right px-3 py-3">{t("budget")}</th>
                  <th className="text-right px-3 py-3">{t("real")}</th>
                  <th className="text-right px-3 py-3">{t("variance")}</th>
                  <th className="text-left px-3 py-3">{t("subcontractor")}</th>
                  <th className="text-right px-3 py-3">%</th>
                  <th className="text-right px-4 py-3">{t("due")}</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {rows.map((r) => (
                  <tr key={r.ticket_id} data-testid={`fin-row-${r.ticket_id}`} onClick={() => setDetailId(r.ticket_id)} className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{r.title}</div>
                      <div className="text-xs text-slate-400">{r.number} · {r.unit_name}</div>
                    </td>
                    <td className="px-3 py-3 text-right text-slate-700">{money(r.budget_total)}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{money(r.actual_total)}</td>
                    <td className={`px-3 py-3 text-right font-medium ${r.variance_total < 0 ? "text-red-600" : "text-emerald-600"}`}>{money(r.variance_total)}</td>
                    <td className="px-3 py-3 text-slate-600">{r.is_subcontractor ? r.subcontractor_name || "—" : <span className="text-slate-400">{t("ownTechnician")}</span>}</td>
                    <td className="px-3 py-3 text-right text-slate-500">{Math.round((r.progress || 0) * 100)}%</td>
                    <td className="px-4 py-3 text-right font-semibold text-fuchsia-600">{r.is_subcontractor ? money(r.sub_due) : "—"}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">{t("noFinancials")}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "property" && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="text-left px-4 py-3">{t("byProperty")}</th>
                  <th className="text-right px-3 py-3">{t("services")}</th>
                  <th className="text-right px-3 py-3">{t("budget")}</th>
                  <th className="text-right px-3 py-3">{t("real")}</th>
                  <th className="text-right px-4 py-3">{t("variance")}</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {(props || []).map((p, i) => (
                  <tr key={p.property_id || i} data-testid={`fin-prop-${p.property_id || i}`} className={`border-b border-slate-100 hover:bg-slate-50 ${p.over_budget ? "bg-red-50/50" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />{p.property_name}
                        {p.over_budget && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3" />{t("overBudget")}</span>}
                      </div>
                      {p.over_budget_jobs > 0 && <div className="text-xs text-red-500 ml-6">{p.over_budget_jobs} {t("services")} {t("overBudget").toLowerCase()}</div>}
                    </td>
                    <td className="px-3 py-3 text-right text-slate-500">{p.jobs}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{money(p.budget_total)}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{money(p.actual_total)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${p.variance_total < 0 ? "text-red-600" : "text-emerald-600"}`}>{money(p.variance_total)}</td>
                  </tr>
                ))}
                {(props || []).length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">{t("noFinancials")}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "client" && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="text-left px-4 py-3">{t("client")}</th>
                  <th className="text-right px-3 py-3">{t("services")}</th>
                  <th className="text-right px-3 py-3">{t("budget")}</th>
                  <th className="text-right px-3 py-3">{t("real")}</th>
                  <th className="text-right px-4 py-3">{t("variance")}</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {(clients || []).map((c, i) => (
                  <tr key={c.client_id || i} data-testid={`fin-client-${c.client_id || i}`} className={`border-b border-slate-100 hover:bg-slate-50 ${c.over_budget ? "bg-red-50/50" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        <UserRound className="w-4 h-4 text-slate-400" />{c.client_name}
                        {c.over_budget && <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3" />{t("overBudget")}</span>}
                      </div>
                      {c.over_budget_jobs > 0 && <div className="text-xs text-red-500 ml-6">{c.over_budget_jobs} {t("services")} {t("overBudget").toLowerCase()}</div>}
                    </td>
                    <td className="px-3 py-3 text-right text-slate-500">{c.jobs}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{money(c.budget_total)}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{money(c.actual_total)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${c.variance_total < 0 ? "text-red-600" : "text-emerald-600"}`}>{money(c.variance_total)}</td>
                  </tr>
                ))}
                {(clients || []).length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">{t("noFinancials")}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detailId && <JobDetailModal ticketId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
